import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { clearCache } from "@/queryClient";
import {
  Building2,
  Check,
  ChevronDown,
  LogOut,
  Plus,
  Settings,
  Trash2,
  User,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { cn } from "@/shared/lib/utils";
import { useAuthStore } from "@/shared/state/authstore";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { logout } from "@/entities/session/api/sessionApi";
import type { MyProfileResponse } from "@/entities/employee/model/types";
import { useRevokedAccounts } from "@/entities/session/hooks/useRevokedAccounts";

interface SidebarUserMenuProps {
  currentUser: MyProfileResponse | undefined;
}

export function SidebarUserMenu({ currentUser }: SidebarUserMenuProps) {
  const navigate = useNavigate();
  const { refreshToken, accountId, availableAccounts } = useAuthStore();
  const { logoutUser, selectAccount } = useAuth();
  const [orgsOpen, setOrgsOpen] = useState(false);
  const revokedIds = new Set(useRevokedAccounts(currentUser).map((a) => a.accountId));

  const userInitials = currentUser
    ? `${currentUser.firstName?.[0] || ""}${currentUser.lastName?.[0] || ""}`
    : "";
  const fullName = currentUser
    ? `${currentUser.firstName} ${currentUser.lastName}`.trim()
    : "";
  const currentAccount = availableAccounts.find(a => a.accountId === accountId);

  const handleClearCache = async () => {
    await clearCache();
    window.location.reload();
  };

  const handleSelectAccount = async (id: number) => {
    if (id === accountId) return;
    await selectAccount(id);
    navigate({ to: "/" });
  };

  const handleLogout = async () => {
    try {
      if (refreshToken) {
        await logout(refreshToken);
      }
    } catch {
      await logoutUser();
      navigate({ to: "/login" });
      return;
    }
    await logoutUser();
    navigate({ to: "/login" });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="flex h-10 w-10 items-center justify-center rounded-md transition-colors hover:bg-hover focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)]"
        >
          <Avatar className="h-8 w-8 border border-line">
            <AvatarImage
              src={currentUser?.profilePictureUrl ?? undefined}
              alt={fullName}
              className="object-contain"
            />
            <AvatarFallback className="bg-brand-accent-soft text-brand-accent-ink text-[11px] font-medium">
              {userInitials || <User size={14} />}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{fullName}</p>
            <p className="text-xs text-muted-foreground leading-none">
              {currentUser?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        {currentAccount && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="font-normal">
              <p className="text-[11px] uppercase tracking-wide text-ink-4">
                Workspace
              </p>
              <p className="mt-0.5 text-[12.5px] font-semibold text-ink leading-tight">
                {currentAccount.accountName}
              </p>
            </DropdownMenuLabel>
          </>
        )}
        <DropdownMenuSeparator />
        <Collapsible open={orgsOpen} onOpenChange={setOrgsOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs font-normal text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:outline-none"
            >
              <span>Organizations</span>
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  !orgsOpen && "-rotate-90",
                )}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <DropdownMenuGroup>
              {availableAccounts.map(account => {
                const isAccessible = account.accessible !== false;
                return (
                  <DropdownMenuItem
                    key={account.accountId}
                    onClick={() => isAccessible && handleSelectAccount(account.accountId)}
                    disabled={!isAccessible}
                    className="cursor-pointer"
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    <span className="flex-1 truncate">{account.accountName}</span>
                    {revokedIds.has(account.accountId) && (
                      <span className="text-xs text-ink-4">Access ended</span>
                    )}
                    {account.accountId === accountId && (
                      <Check className="h-4 w-4 text-brand-accent" />
                    )}
                  </DropdownMenuItem>
                );
              })}
              {availableAccounts.length === 0 && (
                <DropdownMenuItem
                  onClick={() => navigate({ to: "/onboarding/create-org" })}
                  className="cursor-pointer text-brand-accent focus:text-brand-accent-hi"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create new organization
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
          </CollapsibleContent>
        </Collapsible>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => navigate({ to: "/settings" })}
          className="cursor-pointer"
        >
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleClearCache} className="cursor-pointer">
          <Trash2 className="mr-2 h-4 w-4" />
          Clear Cache
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="text-bad focus:text-bad focus:bg-bad-bg cursor-pointer"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
